import React, { useEffect, useState, useRef } from "react";
import * as topojson from "topojson";
import * as d3 from "d3";
import blue from "@material-ui/core/colors/blue";
import grey from "@material-ui/core/colors/grey";

import jsonDataIndonesia from "./IndonesiaProvinceData.json";

let populixProvincesList = [
  "Aceh",
  "Bali",
  "Bangka-Belitung",
  "Banten",
  "Bengkulu",
  "Gorontalo",
  "Jakarta Raya",
  "Jambi",
  "Jawa Barat",
  "Jawa Tengah",
  "Jawa Timur",
  "Kalimantan Barat",
  "Kalimantan Selatan",
  "Kalimantan Tengah",
  "Kalimantan Timur",
  "Kalimantan Utara",
  "Kepulauan Riau",
  "Lampung",
  "Maluku",
  "Maluku Utara",
  "Nusa Tenggara Barat",
  "Nusa Tenggara Timur",
  "Papua",
  "Irian Jaya Barat",
  "Riau",
  "Sulawesi Barat",
  "Sulawesi Selatan",
  "Sulawesi Tengah",
  "Sulawesi Tenggara",
  "Sulawesi Utara",
  "Sumatera Barat",
  "Sumatera Selatan",
  "Sumatera Utara",
  "Yogyakarta",
];

/**
 * range:
 * 0-49
 * 50 - 199
 * 200 - 499
 * 500 - 999
 * 1000 +++
 */
const colorRanges = [
  {
    min: 0,
    max: 0,
    color: grey[500],
  },
  {
    min: 1,
    max: 49,
    color: blue[100],
  },
  {
    min: 50,
    max: 199,
    color: blue[500],
  },
  {
    min: 200,
    max: 499,
    color: blue[700],
  },
  {
    min: 1000,
    max: 99999999,
    color: blue[900],
  },
];

function IndonesiaGeoChart() {
  let width = 1300,
    height = 640,
    centered;
  let scale = 1500;

  const [dataIndonesia, setDataIndonesia] = useState(null);
  const [caseList, setCaseList] = useState([]);

  let d3Container = useRef(null);

  let projection = d3
    .geoEquirectangular()
    .scale(scale)
    .rotate([-120, 0])
    .translate([width / 2, height / 2]);

  let path = d3.geoPath().projection(projection);

  let color = d3
    .scaleThreshold()
    .domain(d3.range(2, 10))
    .range(d3.schemeYlGnBu[9]);

  useEffect(() => {
    (async function fetchingData() {
      setDataIndonesia(jsonDataIndonesia);

      const casesJsonResponse = await fetch(
        "https://api.kawalcorona.com/indonesia/provinsi/"
      );

      const casesList = await casesJsonResponse.json();

      const populixJSON = populixProvincesList.map((province) => {
        let provinceToFind = province;

        // NOTES: special cases
        if (provinceToFind === "Bangka-Belitung")
          provinceToFind = "Kepulauan Bangka Belitung";
        else if (provinceToFind === "Jakarta Raya")
          provinceToFind = "DKI Jakarta";
        else if (provinceToFind === "Yogyakarta")
          provinceToFind = "Daerah Istimewa Yogyakarta";
        else if (provinceToFind === "Irian Jaya Barat")
          provinceToFind = "Papua Barat";

        const found = casesList.find(
          (x) => x.attributes.Provinsi === provinceToFind
        );

        return {
          name: province,
          confirmed: (found && found.attributes.Kasus_Posi) || 0,
          recovered: (found && found.attributes.Kasus_Semb) || 0,
          deaths: (found && found.attributes.Kasus_Meni) || 0,
        };
      });
      const populixJSONList = populixJSON;

      setCaseList(populixJSONList);
    })();
  }, []);

  const [isRendered, setIsRendered] = useState(false);
  useEffect(() => {
    if (!isRendered && dataIndonesia && caseList.length > 0) {
      let svg = d3.select(d3Container.current);
      svg
        .append("rect")
        .attr("class", "background")
        .attr("width", width)
        .attr("height", height);
      // .on("click", clicked);

      let g = svg.append("g");

      const data = dataIndonesia;

      let divTooltip = d3
        .select("body")
        .append("div")
        .attr("id", "tooltip")
        .style("opacity", 0);

      g.append("g")
        .attr("id", "subunits")
        .selectAll("path")
        .data(topojson.feature(data, data.objects.states_provinces).features)
        .enter()
        .append("path")
        .style("stroke", grey[500])
        .style("stroke-width", "1")
        .attr("fill", function (d, i) {
          const found = caseList.find(
            (el) => el.name === d.properties.woe_name
          );

          const confirmedCases = (found && found.confirmed) || 0;

          const selectedColor = colorRanges.find(
            (c) => confirmedCases >= c.min && confirmedCases <= c.max
          );

          return (selectedColor && selectedColor.color) || blue[100];
        })
        .attr("d", path)
        // .on("click", clicked)
        .on("mouseover", function (d) {
          d3.select(this).style("stroke", "orange");

          const found = caseList.find(
            (el) => el.name === d.properties.woe_name
          );

          divTooltip.transition().duration(200).style("opacity", 1);
          divTooltip
            .html(
              "<h3>" +
                "<strong>" +
                d.properties.woe_name +
                "</strong>" +
                " </h3>" +
                "<p>" +
                "<strong>" +
                ((found && found.confirmed) || 0) +
                "</strong>" +
                " Positif COVID-19" +
                " </p>" +
                "<p>" +
                "<strong>" +
                ((found && found.recovered) || 0) +
                "</strong>" +
                " Sembuh" +
                " </p>" +
                "<p>" +
                "<strong>" +
                ((found && found.deaths) || 0) +
                "</strong>" +
                " Meninggal" +
                " </p>"
            )
            .style("left", d3.event.pageX + "px")
            .style("top", d3.event.pageY - 28 + "px");
        })
        .on("mouseout", function (d) {
          d3.select(this).style("stroke", grey[500]);

          divTooltip.transition().duration(500).style("opacity", 0);
        });

      g.append("path")
        .datum(
          topojson.mesh(data, data.objects.subunits, function (a, b) {
            return a !== b;
          })
        )
        .attr("id", "state-borders")
        .attr("d", path);

      function clicked(d) {
        var x, y, k;

        if (d && centered !== d) {
          var centroid = path.centroid(d);
          x = centroid[0];
          y = centroid[1];
          k = 4;
          centered = d;
        } else {
          x = width / 2;
          y = height / 2;
          k = 1;
          centered = null;
        }

        g.selectAll("path").classed(
          "active",
          centered &&
            function (d) {
              return d === centered;
            }
        );

        g.transition()
          .duration(750)
          .attr(
            "transform",
            "translate(" +
              width / 2 +
              "," +
              height / 2 +
              ")scale(" +
              k +
              ")translate(" +
              -x +
              "," +
              -y +
              ")"
          )
          .style("stroke-width", 1.5 / k + "px");
      }

      setIsRendered(true);
    }
  }, [color, dataIndonesia, isRendered, path]);

  return (isRendered &&
    <div style={{
      overflow: 'scroll',
      top: -104,
      position: 'relative',
      marginBottom: -136
    }}>
      <svg
        className="d3-component"
        width={width}
        height={height}
        ref={d3Container}
      />
    </div>
  );
}

export default IndonesiaGeoChart;
